import React, { useEffect, useState } from "react";
import {
  Search, Send, MessageSquare, Check, Clock,
  Briefcase, Globe, Linkedin, UserCheck, Sparkles, ArrowRight,
  TrendingUp, Users, Target, Shield, ExternalLink,
  Mail, FileText, Award, Inbox, Zap, ChevronLeft, ChevronRight,
  PartyPopper, Download, AlertTriangle, X, ThumbsUp,
  CalendarCheck, Lock, Rocket, MessageCircle, Network, Trophy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { CASE_STUDIES, CaseStudyModal } from "./DemoScreens";
import { monthlyResults } from "../data/mock";
import { generateDemoSummaryPDF } from "./generatePDF";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const BJA_LOGO = "https://customer-assets.emergentagent.com/job_jobblast/artifacts/ixjqwc10_image.png";
const fmt = (v, fb = "—") => (v && String(v).trim().length > 0 ? v : fb);

export default function DemoScreens2({ state, actions }) {
  const { currentScreen } = state;
  switch (currentScreen) {
    case 1:  return <S1Welcome state={state} />;
    case 2:  return <S2Qualify state={state} />;
    case 3:  return <S3HowReferralsWork />;
    case 4:  return <S4RevealAndFlow state={state} />;
    case 5:  return <S5WatchLive />;
    case 6:  return <S6Imagine state={state} />;
    case 7:  return <S7Pitch99 state={state} />;
    case 8:  return <S8PermissionUpsell state={state} actions={actions} />;
    case 9:  return <S9MeetBJA state={state} />;
    case 10: return <S10ThreeChannels state={state} />;
    case 11: return <S11LiveGmailChillApply state={state} actions={actions} />;
    case 12: return <S12AppsMath state={state} />;
    case 13: return <S13RealResults state={state} actions={actions} />;
    case 14: return <S14TwoRoads state={state} />;
    case 15: return <S15Bundle state={state} actions={actions} />;
    case 16: return <S16SkepticTestimonial state={state} actions={actions} />;
    case 17: return <S17ObjectionCrusher state={state} />;
    case 18: return <S18WhatHappensAfter state={state} />;
    case 19: return <S19Close state={state} actions={actions} />;
    case 20: return <S20ThankYou state={state} actions={actions} />;
    default: return null;
  }
}

/* ── tiny "yes-ladder" engagement question ── */
function YesQuestion({ text, testid }) {
  return (
    <div className="d2-engage-q" data-testid={testid}>
      <MessageCircle className="w-4 h-4" />
      <span>{text}</span>
    </div>
  );
}

/* ─── 1. Welcome — "Welcome {name}" + 3 promise points ─── */
function S1Welcome({ state }) {
  const { lead } = state;
  const role = fmt(state.confirmedRole || lead.targetRole, "your role");
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-1">
      <div className="d2-welcome-v6">
        <div className="welcome-wave"><Users className="w-6 h-6" /></div>
        <span className="d2-welcome-eyebrow">
          <span className="d2-welcome-eyebrow-label">Welcome,</span> <span className="d2-welcome-eyebrow-name">{lead.clientName}</span>
        </span>
        <h1 className="d2-welcome-headline" data-testid="d2-welcome-headline">
          Get a minimum of <span className="d2-welcome-headline-hl">3 job referrals</span><br/>
          in the next 30 days.
        </h1>
        <p className="d2-welcome-subline">
          Even if recruiters keep ghosting you. Without spending hours on LinkedIn.
        </p>

        <div className="d2-welcome-promise-v6" data-testid="d2-welcome-promise">
          <div className="d2-welcome-promise-head">
            <Rocket className="w-4 h-4" /> By the end of these 20 minutes, you'll have…
          </div>
          <div className="d2-welcome-promise-grid-v6">
            <div className="d2-welcome-promise-item">
              <span className="d2-welcome-promise-num">1</span>
              <span>Why most {role} job hunts take 5+ months — and the one move that fixes it</span>
            </div>
            <div className="d2-welcome-promise-item">
              <span className="d2-welcome-promise-num">2</span>
              <span>The exact system that lands you 3+ referrals in the first 30 days</span>
            </div>
            <div className="d2-welcome-promise-item">
              <span className="d2-welcome-promise-num">3</span>
              <span>A live walk-through on a real account, plus your two clearest paths forward</span>
            </div>
          </div>
        </div>

        <p className="d2-welcome-foot">No fluff, no slide-spam. Let's go.</p>
      </div>
    </div>
  );
}

/* ─── 2. Qualify — 5 fields (no Ref/wk) ────────────── */
function S2Qualify({ state }) {
  const { lead, confirmedRole, confirmedMarket, weeklyApps, linkedInConnections, monthsSearching } = state;
  const askPrompt = "→ ask now";
  const askFmt = (v) => (v && String(v).trim().length > 0 ? v : askPrompt);
  const isEmpty = (v) => !(v && String(v).trim().length > 0);
  const name = `${lead.clientName || ""}${lead.clientLastName ? " " + lead.clientLastName : ""}`.trim() || "—";
  return (
    <div className="demo-screen d2-fit-screen screen-qualify" data-testid="d2-screen-2">
      <h2 className="screen-heading-v2 d2-tight-h">Let's see exactly where you stand</h2>
      <p className="screen-subheading d2-tight-sub">Everything that follows will be tailored to your numbers</p>

      <div className="d2-qual-v9" data-testid="d2-qual-v9">
        {/* LEFT — identity card */}
        <div className="d2-qual-identity">
          <div className="d2-qual-identity-eyebrow">Lead profile</div>
          <div className="d2-qual-identity-name" data-testid="d2-q-name">{name}</div>
          <div className="d2-qual-identity-meta">
            <div className="d2-qual-identity-row">
              <Briefcase className="d2-qual-identity-icon" />
              <span className="d2-qual-identity-label">Target Role</span>
              <span className={`d2-qual-identity-value ${isEmpty(confirmedRole || lead.targetRole) ? "d2-qual-empty" : ""}`} data-testid="d2-q-role">
                {askFmt(confirmedRole || lead.targetRole)}
              </span>
            </div>
            <div className="d2-qual-identity-row">
              <Globe className="d2-qual-identity-icon" />
              <span className="d2-qual-identity-label">Target Market</span>
              <span className={`d2-qual-identity-value ${isEmpty(confirmedMarket || lead.targetMarket) ? "d2-qual-empty" : ""}`} data-testid="d2-q-market">
                {askFmt(confirmedMarket || lead.targetMarket)}
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT — activity numbers */}
        <div className="d2-qual-activity">
          <div className="d2-qual-activity-eyebrow">Activity so far</div>
          <div className="d2-qual-activity-grid">
            <div className="d2-qual-stat">
              <Send className="d2-qual-stat-icon" />
              <div className={`d2-qual-stat-num ${isEmpty(weeklyApps) ? "d2-qual-stat-num-empty" : ""}`} data-testid="d2-q-apps">{askFmt(weeklyApps)}</div>
              <div className="d2-qual-stat-lbl">Appl. per week</div>
            </div>
            <div className="d2-qual-stat">
              <Linkedin className="d2-qual-stat-icon" />
              <div className={`d2-qual-stat-num ${isEmpty(linkedInConnections) ? "d2-qual-stat-num-empty" : ""}`} data-testid="d2-q-li">{askFmt(linkedInConnections)}</div>
              <div className="d2-qual-stat-lbl">LinkedIn requests / week</div>
            </div>
            <div className="d2-qual-stat">
              <Clock className="d2-qual-stat-icon" />
              <div className={`d2-qual-stat-num ${isEmpty(monthsSearching) ? "d2-qual-stat-num-empty" : ""}`} data-testid="d2-q-months">{askFmt(monthsSearching)}</div>
              <div className="d2-qual-stat-lbl">Months searching</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── 3. How Referrals Work ─────────────────────────── */
function S3HowReferralsWork() {
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-3">
      <h2 className="screen-heading-v2 d2-tight-h" style={{ textAlign: "center" }}>How referrals actually work</h2>
      <p className="screen-subheading d2-tight-sub" style={{ textAlign: "center" }}>Two angles nobody explains: how to get one, and why people give one</p>

      <div className="d2-ref-section d2-ref-section-tight">
        <h3 className="d2-ref-section-title">Three ways someone can refer you</h3>
        <div className="d2-ref-channel-grid">
          <div className="d2-ref-channel">
            <div className="d2-ref-channel-num">1</div>
            <div className="d2-ref-channel-name">Internal portal</div>
            <div className="d2-ref-channel-desc">They paste your link into their company's referral tool. 30 seconds. HR auto-routes it.</div>
          </div>
          <div className="d2-ref-channel">
            <div className="d2-ref-channel-num">2</div>
            <div className="d2-ref-channel-name">Direct DM to recruiter</div>
            <div className="d2-ref-channel-desc">"This person looks like a fit." That single line bypasses the cold-applicant stack.</div>
          </div>
          <div className="d2-ref-channel">
            <div className="d2-ref-channel-num">3</div>
            <div className="d2-ref-channel-name">Hiring manager intro</div>
            <div className="d2-ref-channel-desc">Warm intro to the actual decision maker. Highest conversion path. Our system makes it easy.</div>
          </div>
        </div>
      </div>

      <div className="d2-ref-section d2-ref-section-tight">
        <h3 className="d2-ref-section-title">What's in it for the person referring you</h3>
        <div className="d2-ref-benefit-grid">
          <div className="d2-ref-benefit">
            <Award className="d2-ref-benefit-icon" />
            <div>
              <div className="d2-ref-benefit-name">Referral bonus</div>
              <div className="d2-ref-benefit-desc">$2K–$10K paid when their referral is hired. Real money for one click.</div>
            </div>
          </div>
          <div className="d2-ref-benefit">
            <TrendingUp className="d2-ref-benefit-icon" />
            <div>
              <div className="d2-ref-benefit-name">Manager points</div>
              <div className="d2-ref-benefit-desc">Bringing in talent makes them look good internally. Career capital.</div>
            </div>
          </div>
          <div className="d2-ref-benefit">
            <Users className="d2-ref-benefit-icon" />
            <div>
              <div className="d2-ref-benefit-name">Network growth</div>
              <div className="d2-ref-benefit-desc">Helping you costs nothing and builds long-term goodwill.</div>
            </div>
          </div>
        </div>
      </div>

      <YesQuestion testid="d2-engage-s3" text="Make sense? Referrals are an exchange, not a favor." />
    </div>
  );
}

/* ─── 4. Reveal + Flow ──────────────────────────── */
function S4RevealAndFlow({ state }) {
  const role = fmt(state.confirmedRole || state.lead.targetRole, "your role");
  const NODES = [
    { icon: <Search className="w-4 h-4" />, t: "Finds the right job", d: `Scans LinkedIn 24/7 for ${role} openings.` },
    { icon: <Target className="w-4 h-4" />, t: "Finds the right referrer", d: "Pulls recruiters and team members likely to respond." },
    { icon: <ThumbsUp className="w-4 h-4" />, t: "Likes their last 2 posts", d: "Warms them up before you reach out." },
    { icon: <Send className="w-4 h-4" />, t: "Drafts a connection request", d: "Personalized using what they posted. You review and send in one tap." },
    { icon: <Check className="w-4 h-4" />, t: "Tracks acceptances daily", d: "Once accepted, drafts the referral ask and reminds you to follow up." },
    { icon: <Mail className="w-4 h-4" />, t: "Drops Referral Kit", d: "One link, they refer you in 30 seconds." },
    { icon: <TrendingUp className="w-4 h-4" />, t: "Tracks every step", d: "Live kanban CRM. You see who's warm, who replied." },
  ];
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-4">
      <div className="d2-reveal-wrap-v8">
        <span className="d2-reveal-eyebrow">
          <Sparkles className="w-4 h-4 inline mr-1" /> Imagine if every day, automatically...
        </span>
        <h1 className="d2-reveal-title-v8">
          ChillRefer did the heavy lifting for your <span className="d2-reveal-role">{role}</span> search.
        </h1>
        <p className="d2-reveal-tag-v8">
          7 things, every single day. You stay in control of every message that goes out.
        </p>
      </div>

      <div className="d2-flow-grid-v8" data-testid="d2-flow-canvas">
        {NODES.map((n, i) => (
          <div key={i} className="d2-flow-node-v8" data-testid={`d2-node-${i + 1}`}>
            <div className="d2-flow-node-head-v8">
              <span className="d2-flow-node-num-v8">{i + 1}</span>
              <span className="d2-flow-node-icon-v8">{n.icon}</span>
            </div>
            <div className="d2-flow-node-title-v8">{n.t}</div>
            <div className="d2-flow-node-desc-v8">{n.d}</div>
          </div>
        ))}
      </div>

      <div className="d2-flow-stats-v8">
        <div className="d2-flow-stat-v8"><strong>20</strong>connects / day</div>
        <div className="d2-flow-stat-v8"><strong>40</strong>follow-ups / day</div>
        <div className="d2-flow-stat-v8"><strong>600+</strong>recruiters / mo</div>
        <div className="d2-flow-stat-v8"><strong>0</strong>copy-paste from you</div>
      </div>
    </div>
  );
}

/* ─── 5. Watch It Live ─────────────────────────────── */
function S5WatchLive() {
  const open = () => window.open("https://chillrefer.com", "_blank", "noopener,noreferrer");
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-5">
      <div className="d2-live-wrap">
        <div className="d2-live-card">
          <span className="d2-live-eyebrow">Live demo</span>
          <h2 className="d2-live-title">Forget the slides for a moment.</h2>
          <p className="d2-live-sub">Let me walk you through ChillRefer on a real account, right now.</p>
          <Button onClick={open} className="d2-live-cta" data-testid="d2-open-chillrefer">
            <ExternalLink className="w-4 h-4" /> Open ChillRefer
          </Button>
          <a href="https://chillrefer.com" target="_blank" rel="noopener noreferrer" className="d2-live-fallback">
            or right-click and open in new tab
          </a>
        </div>
      </div>
      <YesQuestion testid="d2-engage-s5" text="Now that you've seen it live — does this feel real to you?" />
    </div>
  );
}

/* ─── 6. Imagine 30 Days — single hero card ─── */
function S6Imagine({ state }) {
  const name = state.lead.clientName;
  const role = fmt(state.confirmedRole || state.lead.targetRole, "your role");
  const market = fmt(state.confirmedMarket || state.lead.targetMarket, "your market");
  return (
    <div className="demo-screen d2-fit-screen d2-imagine-screen" data-testid="d2-screen-6">
      <div className="d2-imagine-wrap-v6">
        <span className="d2-imagine-eyebrow">
          <CalendarCheck className="w-4 h-4 inline mr-1" /> 30 days from today
        </span>
        <h1 className="d2-imagine-headline">
          Picture this, {name}…
        </h1>
        <p className="d2-imagine-sub">
          Same person. Same {role} target. Same {market} market. One shift in the system behind you.
        </p>
        <div className="d2-imagine-hero-card" data-testid="d2-imagine-hero">
          <div className="d2-imagine-hero-num">3–5</div>
          <div className="d2-imagine-hero-name">Real referrals landed</div>
          <div className="d2-imagine-hero-desc">
            Three to five actual referrers said "yes" and pasted you into the system. Not "I'll think about it" — done. Each one a 9× better shot at the role than a cold application.
          </div>
        </div>
        <p className="d2-imagine-foot">
          That's not a fantasy. That's the math when ChillRefer fires every single day.
        </p>
      </div>
    </div>
  );
}

/* ─── 7. $99 Pitch — VS comparison ──────────────── */
function S7Pitch99({ state }) {
  const name = state.lead.clientName;
  const role = fmt(state.confirmedRole || state.lead.targetRole, "your role");
  const market = fmt(state.confirmedMarket || state.lead.targetMarket, "your market");
  const ROWS = [
    { label: "Hours per month",          manual: "180+ hrs",          cr: "1 hr (review drafts)" },
    { label: "Recruiter outreaches",     manual: "20–40 max",         cr: "600+ personalized" },
    { label: "Personalization quality",  manual: "Copy-paste",        cr: "AI reads last 2 posts" },
    { label: "Follow-up consistency",    manual: "You forget",        cr: "Auto every 24h, 4 max" },
    { label: "Risk",                     manual: "All of it",         cr: "Cancel anytime" },
  ];
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-7">
      <h2 className="screen-heading-v2 d2-tight-h" style={{ textAlign: "center" }}>Doing this manually vs ChillRefer doing it for you</h2>
      <p className="screen-subheading d2-tight-sub" style={{ textAlign: "center" }}>
        Tailored to {name}'s search for {role} in {market}
      </p>

      <div className="d2-vs-wrap">
        <table className="d2-vs-table">
          <thead>
            <tr>
              <th></th>
              <th>You, doing it manually</th>
              <th className="d2-vs-col-win">With ChillRefer</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.label}>
                <td className="d2-vs-row-label">{r.label}</td>
                <td className="d2-vs-cell-manual">{r.manual}</td>
                <td className="d2-vs-cell-win">{r.cr}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d2-vs-cta">
          <div className="d2-vs-cta-price">$99 / month</div>
          <div className="d2-vs-cta-tag">Locked in today. Cancel anytime. Live in under an hour.</div>
        </div>
      </div>
    </div>
  );
}

/* ─── 8. Permission Upsell — "5 mins?" ─────────── */
function S8PermissionUpsell({ state, actions }) {
  const name = state.lead.clientName;
  return (
    <div className="demo-screen d2-fit-screen d2-perm-screen" data-testid="d2-screen-8">
      <div className="d2-perm-wrap">
        <div className="d2-perm-icon">
          <MessageSquare className="w-9 h-9" />
        </div>
        <span className="d2-perm-eyebrow">Permission to share</span>
        <h2 className="d2-perm-title">
          {name}, before you decide on ChillRefer…
        </h2>
        <p className="d2-perm-body">
          We also have a <strong>Done-For-You service</strong> where our team applies for you, runs ChillRefer in the background, and guarantees interviews.
        </p>
        <div className="d2-perm-card">
          <Clock className="d2-perm-card-icon" />
          <div>
            <div className="d2-perm-card-title">Mind if I take 5 minutes to walk you through it?</div>
            <div className="d2-perm-card-sub">Worst case, you skip and keep ChillRefer at $99. Best case, you save 5 months.</div>
          </div>
        </div>
        <div className="d2-perm-actions">
          <Button onClick={() => actions.setCurrentScreen(9)} className="d2-perm-yes" data-testid="d2-perm-yes">
            Yes, show me <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button onClick={() => actions.setCurrentScreen(17)} variant="outline" className="d2-perm-skip" data-testid="d2-perm-skip">
            Skip, lock in $99
          </Button>
        </div>
        <p className="d2-perm-foot">If you skip, we go straight to checkout for ChillRefer only.</p>
      </div>
    </div>
  );
}

/* ─── 9. Meet BJA — clean logo card, tighter layout, personalized ───── */
function S9MeetBJA({ state }) {
  const name = state?.lead?.clientName || "you";
  const role = state?.confirmedRole || state?.lead?.targetRole;
  const market = state?.confirmedMarket || state?.lead?.targetMarket;
  const months = state?.monthsSearching;

  // Build a human-feeling intro that gracefully handles missing fields
  const personalBits = [];
  if (role) personalBits.push(`a ${role}`);
  if (market) personalBits.push(`in ${market}`);
  if (months && parseFloat(months) > 0) {
    personalBits.push(`${months} months into the hunt`);
  }
  const personalLine = personalBits.length > 0
    ? `Made for ${personalBits.join(", ")}.`
    : `Made for serious job hunters who want to move fast.`;

  return (
    <div className="demo-screen d2-fit-screen d2-bja-screen-v7" data-testid="d2-screen-9">
      <div className="d2-bja-hero-v7">
        {/* Branded text logo — always visible on green hero */}
        <div className="d2-bja-logo-pill" data-testid="d2-bja-logo-wrap">
          <Briefcase className="d2-bja-logo-mark" />
          <span className="d2-bja-logo-text" data-testid="d2-bja-logo">BulkJobApply</span>
        </div>
        <span className="d2-bja-program-tag">The 90-day done-for-you program</span>
        <h2 className="d2-bja-hero-title-v7">
          {name}, this is built for someone exactly like you.
        </h2>
        <p className="d2-bja-hero-sub-v7">
          {personalLine} You bring the skill. We bring the team — real humans applying, AI running referrals 24/7, and a fully rebuilt LinkedIn. All under one roof, all working hard for you.
        </p>

        <div className="d2-bja-hero-stats-v7">
          <div className="d2-bja-stat-v7"><div className="d2-bja-stat-num">10</div><div className="d2-bja-stat-lbl">interviews guaranteed</div></div>
          <div className="d2-bja-stat-v7"><div className="d2-bja-stat-num">90</div><div className="d2-bja-stat-lbl">days to land</div></div>
          <div className="d2-bja-stat-v7"><div className="d2-bja-stat-num">780+</div><div className="d2-bja-stat-lbl">appl. / month</div></div>
          <div className="d2-bja-stat-v7"><div className="d2-bja-stat-num">100%</div><div className="d2-bja-stat-lbl">salary stays yours</div></div>
        </div>

        <div className="d2-bja-pillars-v7">
          <div className="d2-bja-pillar-v7">
            <div className="d2-bja-pillar-icon-v7" style={{ background: "#fee2e2", color: "#b91c1c" }}><Users className="w-4 h-4" /></div>
            <div className="d2-bja-pillar-name-v7">Real human team</div>
            <div className="d2-bja-pillar-desc-v7">Specialists, HR, account manager.</div>
          </div>
          <div className="d2-bja-pillar-v7">
            <div className="d2-bja-pillar-icon-v7" style={{ background: "#fef3c7", color: "#b45309" }}><FileText className="w-4 h-4" /></div>
            <div className="d2-bja-pillar-name-v7">Resume rebuild</div>
            <div className="d2-bja-pillar-desc-v7">HR pro rewrites in 48 hours.</div>
          </div>
          <div className="d2-bja-pillar-v7">
            <div className="d2-bja-pillar-icon-v7" style={{ background: "#dcfce7", color: "#15803d" }}><MessageSquare className="w-4 h-4" /></div>
            <div className="d2-bja-pillar-name-v7">Your team on WhatsApp</div>
            <div className="d2-bja-pillar-desc-v7">Daily updates. Weekly reports.</div>
          </div>
          <div className="d2-bja-pillar-v7">
            <div className="d2-bja-pillar-icon-v7" style={{ background: "#ede9fe", color: "#6d28d9" }}><Sparkles className="w-4 h-4" /></div>
            <div className="d2-bja-pillar-name-v7">ChillRefer + ChillApply</div>
            <div className="d2-bja-pillar-desc-v7">Both AI engines run in parallel.</div>
          </div>
        </div>

        <div className="d2-bja-promise-v7">
          <Shield className="w-5 h-5" />
          <span><strong>The Hired-in-90 Promise:</strong> 10 interviews in 90 days, or 1 year of ChillRefer free until you land.</span>
        </div>
      </div>
    </div>
  );
}

/* ─── 10. 3 Channels — restructured under BJA umbrella ── */
function S10ThreeChannels({ state }) {
  const role = fmt(state?.confirmedRole || state?.lead?.targetRole, "your role");
  const market = fmt(state?.confirmedMarket || state?.lead?.targetMarket, "your market");
  const CHANNELS = [
    {
      num: "01",
      icon: <Send className="w-5 h-5" />,
      tint: "#fee2e2",
      tintColor: "#b91c1c",
      product: "BulkJobApply",
      title: "Job applications, done by humans",
      desc: "Up to 780 tailored applications per month to company career pages. Real humans, ATS-friendly resumes, every application customized to the role.",
      stat: "780/mo",
      statLbl: "applications by humans",
    },
    {
      num: "02",
      icon: <Network className="w-5 h-5" />,
      tint: "#ede9fe",
      tintColor: "#6d28d9",
      product: "ChillRefer",
      title: "Networking & referrals on autopilot",
      desc: "600+ personalized recruiter and hiring-manager outreaches every month. AI-driven, custom per recruiter — gets you in through the warm door.",
      stat: "600/mo",
      statLbl: "recruiter outreaches",
    },
    {
      num: "03",
      icon: <Linkedin className="w-5 h-5" />,
      tint: "#dbeafe",
      tintColor: "#1e40af",
      product: "LinkedIn Optimization + ChillApply",
      title: "Visibility & inbound interviews",
      desc: "Profile rebuilt for ATS and humans. ChillApply auto-applies to LinkedIn Easy Apply jobs in the background. Recruiters start finding you.",
      stat: "4,000+",
      statLbl: "Easy Apply jobs / mo",
    },
  ];
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-10">
      <div className="d2-channels-header">
        <h2 className="screen-heading-v2 d2-tight-h" style={{ textAlign: "center", marginBottom: 4 }}>The 3 channels that get you interviews</h2>
        <p className="screen-subheading d2-tight-sub" style={{ textAlign: "center", margin: 0 }}>
          For your <strong>{role}</strong> search in <strong>{market}</strong> — Hired in 90 fires all three in parallel. Most candidates run only one. That's why they wait 5+ months.
        </p>
      </div>
      <div className="d2-channels-flow" data-testid="d2-channels-flow">
        {CHANNELS.map((c, i) => (
          <React.Fragment key={i}>
            <div className="d2-channel-card" data-testid={`d2-channel-${i + 1}`}>
              <div className="d2-channel-top">
                <span className="d2-channel-num">{c.num}</span>
                <div className="d2-channel-icon" style={{ background: c.tint, color: c.tintColor }}>{c.icon}</div>
              </div>
              <div className="d2-channel-product" style={{ color: c.tintColor }}>{c.product}</div>
              <div className="d2-channel-title">{c.title}</div>
              <div className="d2-channel-desc">{c.desc}</div>
              <div className="d2-channel-stat">
                <span className="d2-channel-stat-num">{c.stat}</span>
                <span className="d2-channel-stat-lbl">{c.statLbl}</span>
              </div>
            </div>
            {i < CHANNELS.length - 1 && (
              <div className="d2-channel-plus">+</div>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="d2-channels-foot">
        <Trophy className="w-5 h-5" />
        <span>All 3 firing for the same lead = average <strong>3 months to land</strong> across 1,500+ clients.</span>
      </div>
    </div>
  );
}

/* ─── 11. Live Gmail + ChillApply ────────────────── */
function S11LiveGmailChillApply({ state, actions }) {
  const { gmailMode, lead } = state;
  if (gmailMode) {
    return (
      <div className="demo-screen d2-fit-screen screen-proof gmail-active" data-testid="d2-screen-11">
        <div className="gmail-reveal-overlay">
          <div className="gmail-pulse-dot" />
          <h2>Showing live client inbox...</h2>
          <p>Switch tabs now to show real interview replies</p>
          <Button onClick={() => actions.setGmailMode(false)} variant="outline" className="mt-4" data-testid="d2-back-gmail">
            Back to demo
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-11">
      <h2 className="screen-heading-v2 d2-tight-h" style={{ textAlign: "center" }}>See it actually working</h2>
      <p className="screen-subheading d2-tight-sub" style={{ textAlign: "center" }}>Two real things, on real accounts. Pick what you want to see.</p>
      <div className="d2-live-twin">
        <div className="d2-live-twin-card">
          <div className="d2-live-twin-icon" style={{ background: "#fef3c7", color: "#b45309" }}><Inbox className="w-5 h-5" /></div>
          <h3 className="d2-live-twin-title">Real client interview inbox</h3>
          <p className="d2-live-twin-desc">A live Gmail with actual interview emails from companies hiring our clients. Microsoft, Stripe, Amazon — real reply-alls.</p>
          <Button onClick={() => actions.setGmailMode(true)} className="d2-live-twin-btn" style={{ background: "#b45309" }} data-testid="d2-show-gmail">
            <Mail className="w-4 h-4" /> Open live inbox
          </Button>
        </div>
        <div className="d2-live-twin-card">
          <div className="d2-live-twin-icon" style={{ background: "#dbeafe", color: "#1e40af" }}><Zap className="w-5 h-5" /></div>
          <h3 className="d2-live-twin-title">ChillApply running live</h3>
          <p className="d2-live-twin-desc">Auto-applies to LinkedIn Easy Apply jobs that match {lead.clientName}'s exact filter. Runs quietly in the background while you focus on interviews.</p>
          <Button onClick={() => window.open("https://chillapply.com", "_blank", "noopener,noreferrer")} className="d2-live-twin-btn" style={{ background: "#1e40af" }} data-testid="d2-open-chillapply">
            <ExternalLink className="w-4 h-4" /> Open ChillApply
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── 12. Apps Math + small results strip ───────── */
function S12AppsMath({ state }) {
  const apps = parseFloat(state.weeklyApps) || 10;
  const months = parseFloat(state.monthsSearching) || 1;
  const yourMonthly = apps * 4;
  const totalSoFar = yourMonthly * months;

  // Derive monthly salary from lead.targetSalary if present, else $8K baseline
  // Accepts numbers like 120000, "120k", "$120,000", "12 LPA" (last is treated as 0 → fallback)
  const parseAnnualSalary = (raw) => {
    if (!raw) return null;
    const s = String(raw).toLowerCase().replace(/[$,\s]/g, "");
    if (/lpa|lakh/.test(s)) return null; // skip Indian-style salary, fallback baseline
    let n = parseFloat(s.replace(/k/g, "")) || 0;
    if (s.includes("k")) n *= 1000;
    return n > 1000 ? n : null;
  };
  const annualSalary = parseAnnualSalary(state.lead?.targetSalary);
  const monthlySalary = annualSalary ? Math.round(annualSalary / 12) : 8000;
  const lostSalary = months * monthlySalary;
  const bjaApps = 780;
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-12">
      <h2 className="screen-heading-v2 d2-tight-h" style={{ textAlign: "center" }}>Your numbers vs ours</h2>
      <p className="screen-subheading d2-tight-sub" style={{ textAlign: "center" }}>Brutal honesty. Then a way out.</p>

      <div className="d2-pain-card d2-pain-card-tight" data-testid="d2-pain-card">
        <div className="d2-pain-eyebrow"><AlertTriangle className="w-4 h-4" /> Where you are right now</div>
        <div className="d2-pain-stats">
          <div className="d2-pain-stat">
            <div className="d2-pain-num">{totalSoFar.toLocaleString()}</div>
            <div className="d2-pain-lbl">appl. sent in {months} {months === 1 ? "month" : "months"}</div>
          </div>
          <div className="d2-pain-divider" />
          <div className="d2-pain-stat">
            <div className="d2-pain-num d2-pain-red">${lostSalary.toLocaleString()}</div>
            <div className="d2-pain-lbl">
              salary lost while searching
              {annualSalary
                ? <span className="d2-pain-baseline-note"> · based on your ${(monthlySalary).toLocaleString()}/mo target</span>
                : <span className="d2-pain-baseline-note"> · using $8K/mo baseline</span>}
            </div>
          </div>
          <div className="d2-pain-divider" />
          <div className="d2-pain-stat">
            <div className="d2-pain-num">{months}+</div>
            <div className="d2-pain-lbl">months and counting</div>
          </div>
        </div>
      </div>

      <div className="d2-relief-card d2-relief-card-tight" data-testid="d2-relief-card">
        <div className="d2-relief-eyebrow"><Sparkles className="w-4 h-4" /> Where you'd be with Hired in 90</div>
        <div className="d2-relief-stats">
          <div className="d2-relief-stat">
            <div className="d2-relief-num">{bjaApps}</div>
            <div className="d2-relief-lbl">appl. / month</div>
          </div>
          <div className="d2-relief-divider" />
          <div className="d2-relief-stat">
            <div className="d2-relief-num">3 mo</div>
            <div className="d2-relief-lbl">avg time to land</div>
          </div>
          <div className="d2-relief-divider" />
          <div className="d2-relief-stat">
            <div className="d2-relief-num">{Math.round(bjaApps / Math.max(yourMonthly, 1))}×</div>
            <div className="d2-relief-lbl">more appl.</div>
          </div>
        </div>
      </div>

      {/* Compact results strip */}
      <div className="d2-math-results-strip" data-testid="d2-math-results-strip">
        <span className="d2-math-results-eyebrow">Across 1,500+ clients:</span>
        <div className="d2-math-results-row">
          <div className="d2-math-result"><strong>94%</strong>got interviews</div>
          <div className="d2-math-result"><strong>18 days</strong>to first interview</div>
          <div className="d2-math-result"><strong>300+</strong>interviews / month</div>
          <div className="d2-math-result"><strong>5,00,000+</strong>appl. sent</div>
        </div>
      </div>
    </div>
  );
}

/* ─── 13. Real Results — fixed month row ───────── */
function S13RealResults({ state, actions }) {
  const { selectedCaseStudies = [], proofTab, pdfMonth, pdfPage, pdfTotalPages } = state;
  const [imageUrl, setImageUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [activeCaseStudy, setActiveCaseStudy] = useState(null);

  const currentMonthData = monthlyResults[pdfMonth || 0];

  useEffect(() => {
    if (proofTab !== "results" || !currentMonthData?.pdfUrl) return;
    setLoadingPdf(true);
    setPdfError(null);
    fetch(`${backendUrl}/api/pdf-info?url=${encodeURIComponent(currentMonthData.pdfUrl)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`PDF info failed: ${r.status}`);
        return r.json();
      })
      .then((d) => { actions.setPdfTotalPages(d.page_count || 50); actions.setPdfPage(1); })
      .catch((e) => { setPdfError(e.message); actions.setPdfTotalPages(50); })
      .finally(() => setLoadingPdf(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfMonth, proofTab, currentMonthData?.pdfUrl]);

  useEffect(() => {
    if (!currentMonthData?.pdfUrl) return;
    setImageLoadFailed(false);
    setImageUrl(`${backendUrl}/api/pdf-page-image?url=${encodeURIComponent(currentMonthData.pdfUrl)}&page=${pdfPage || 1}&scale=1.2&quality=75`);
  }, [pdfPage, pdfMonth, currentMonthData?.pdfUrl]);

  const handleCardClick = (cs) => {
    actions.toggleCaseStudy(cs.id);
    setActiveCaseStudy(cs);
  };

  const retryPdf = () => {
    setPdfError(null);
    setImageLoadFailed(false);
    if (currentMonthData?.pdfUrl) {
      setImageUrl(`${backendUrl}/api/pdf-page-image?url=${encodeURIComponent(currentMonthData.pdfUrl)}&page=${pdfPage || 1}&scale=1.2&quality=75&t=${Date.now()}`);
    }
  };

  return (
    <div className="demo-screen d2-fit-screen screen-proof" data-testid="d2-screen-13">
      <h2 className="screen-heading-v2 d2-tight-h">Real results, real people</h2>

      <div className="s3-tabs">
        <button className={`s3-tab ${(proofTab || "pain") === "pain" ? "s3-tab-active" : ""}`} onClick={() => actions.setProofTab("pain")} data-testid="d2-proof-tab-pain">
          Client Stories ({selectedCaseStudies.length} selected)
        </button>
        <button className={`s3-tab ${proofTab === "results" ? "s3-tab-active" : ""}`} onClick={() => actions.setProofTab("results")} data-testid="d2-proof-tab-results">
          Monthly Results
        </button>
      </div>

      {(proofTab || "pain") === "pain" && (
        <div className="s3-case-grid" data-testid="d2-case-study-grid">
          {CASE_STUDIES.map((cs) => {
            const selected = selectedCaseStudies.includes(cs.id);
            return (
              <button key={cs.id} className={`s3-case-tile ${selected ? "s3-case-selected" : ""}`} onClick={() => handleCardClick(cs)} data-testid={`d2-case-study-${cs.id}`}>
                <div className="s3-case-top">
                  <span className="s3-case-tag">{cs.tag}</span>
                  {selected && <Check className="s3-case-check" />}
                </div>
                <span className="s3-case-name">{cs.name}</span>
                <span className="s3-case-role">{cs.role} · {cs.market}</span>
                <span className="s3-case-pain">{cs.painPoint}</span>
                {selected && (
                  <div className="s3-case-result">
                    <span className="s3-case-result-text">{cs.result}</span>
                    <span className="s3-case-interviews">{cs.interviews} interviews</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {proofTab === "results" && (
        <div className="s3-pdf-viewer" data-testid="d2-pdf-viewer">
          <div className="d2-pdf-months-scroll">
            {monthlyResults.map((m, i) => (
              <button
                key={m.id}
                className={`s3-pdf-month-btn d2-pdf-month-btn-tight ${(pdfMonth || 0) === i ? "s3-pdf-month-active" : ""}`}
                onClick={() => { actions.setPdfMonth(i); actions.setPdfPage(1); }}
                data-testid={`d2-pdf-month-${m.id}`}
              >
                {m.month.slice(0, 3)} {m.year.slice(2)}
              </button>
            ))}
          </div>
          <div className="s3-pdf-display">
            {loadingPdf && <div className="s3-pdf-loading">Loading {currentMonthData?.month} report...</div>}
            {pdfError && !loadingPdf && (
              <div className="d2-pdf-error">
                <AlertTriangle className="w-5 h-5" />
                <div>
                  <strong>Couldn't load this report.</strong>
                  <p>{pdfError}</p>
                  <button onClick={retryPdf} className="d2-pdf-retry">Retry</button>
                </div>
              </div>
            )}
            {!loadingPdf && !pdfError && imageUrl && !imageLoadFailed && (
              <img
                src={imageUrl}
                alt={`${currentMonthData?.month} results`}
                className="s3-pdf-image"
                onError={() => setImageLoadFailed(true)}
              />
            )}
            {imageLoadFailed && !loadingPdf && (
              <div className="d2-pdf-error">
                <AlertTriangle className="w-5 h-5" />
                <div>
                  <strong>Report image didn't render.</strong>
                  <p>The PDF backend might be slow. Try again in a second.</p>
                  <button onClick={retryPdf} className="d2-pdf-retry">Retry</button>
                </div>
              </div>
            )}
          </div>
          <div className="s3-pdf-nav">
            <button className="s3-pdf-nav-btn" disabled={(pdfPage || 1) <= 1} onClick={() => actions.setPdfPage((pdfPage || 1) - 1)} data-testid="d2-pdf-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="s3-pdf-page-info">Page {pdfPage || 1} of {pdfTotalPages || "..."}</span>
            <button className="s3-pdf-nav-btn" disabled={(pdfPage || 1) >= (pdfTotalPages || 50)} onClick={() => actions.setPdfPage((pdfPage || 1) + 1)} data-testid="d2-pdf-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {activeCaseStudy && <CaseStudyModal cs={activeCaseStudy} onClose={() => setActiveCaseStudy(null)} />}
    </div>
  );
}

/* ─── 14. Two Roads — sit-idle vs get-hired emotional close ─── */
function S14TwoRoads({ state }) {
  const name = state.lead.clientName;
  const months = state.monthsSearching || "a few";
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-14">
      <div className="d2-roads-wrap">
        <span className="d2-roads-eyebrow">
          <Sparkles className="w-4 h-4 inline mr-1" /> The honest fork in the road
        </span>
        <h2 className="d2-roads-title">
          {name}, from here you have two roads.
        </h2>
        <p className="d2-roads-sub">
          Same person. Same skills. Same {months}-month track record. Different next 90 days.
        </p>

        <div className="d2-roads-grid">
          <div className="d2-road-card d2-road-bad" data-testid="d2-road-stay">
            <span className="d2-road-tag">Road 1 — keep things the same</span>
            <h3 className="d2-road-title">Stay where you are.</h3>
            <ul className="d2-road-list">
              <li><span className="d2-road-bullet d2-road-bullet-bad">×</span>Apps disappear into the void at 11pm. Same as last month.</li>
              <li><span className="d2-road-bullet d2-road-bullet-bad">×</span>Another 3–5 months of "we're moving forward with other candidates."</li>
              <li><span className="d2-road-bullet d2-road-bullet-bad">×</span>Savings keep dwindling. Bills don't.</li>
              <li><span className="d2-road-bullet d2-road-bullet-bad">×</span>Same role. Same {months}-month wait. Just longer.</li>
            </ul>
            <div className="d2-road-foot d2-road-foot-bad">
              The hidden cost of "I'll keep trying" is roughly <strong>$8K/month</strong> in lost salary.
            </div>
          </div>

          <div className="d2-road-card d2-road-good" data-testid="d2-road-go">
            <span className="d2-road-best">Most clients pick this</span>
            <span className="d2-road-tag">Road 2 — let our team take it from here</span>
            <h3 className="d2-road-title">Move forward with us.</h3>
            <ul className="d2-road-list">
              <li><span className="d2-road-bullet d2-road-bullet-good"><Check className="w-3 h-3" /></span>Day 1: resume rebuild kicks off, ChillRefer fires, application pipeline starts filling.</li>
              <li><span className="d2-road-bullet d2-road-bullet-good"><Check className="w-3 h-3" /></span>Week 1: first interview emails landing in your inbox.</li>
              <li><span className="d2-road-bullet d2-road-bullet-good"><Check className="w-3 h-3" /></span>Day 90: hired, or 1 year of ChillRefer free until you are.</li>
              <li><span className="d2-road-bullet d2-road-bullet-good"><Check className="w-3 h-3" /></span>You keep 100% of your salary. No %, no kickback.</li>
            </ul>
            <div className="d2-road-foot d2-road-foot-good">
              <Shield className="w-4 h-4 inline mr-1" /> The Hired-in-90 Promise.
            </div>
          </div>
        </div>

        <p className="d2-roads-foot">
          Both are valid choices. One has a known cost. The other has a known timeline.
        </p>
      </div>
    </div>
  );
}

/* ─── 15. Bundle — value stack only, pay-block as horizontal strip ── */
function S15Bundle({ state, actions }) {
  const { selectedClose } = state;
  const isCR = selectedClose === "cr";
  const isH90 = selectedClose === "h90_a" || selectedClose === "h90_c";

  return (
    <div className="demo-screen d2-fit-screen d2-bundle-screen-v7" data-testid="d2-screen-15">
      <h2 className="screen-heading-v2 d2-tight-h" style={{ textAlign: "center" }}>Two ways to start, {state.lead.clientName}</h2>
      <p className="screen-subheading d2-tight-sub" style={{ textAlign: "center" }}>Pick the level of commitment that fits where you are right now</p>

      <div className="d2-bundle-grid-v7" data-testid="d2-h90-grid">

        {/* PLAN 1 — ChillRefer (compact) */}
        <div className={`d2-bundle-card-v7 d2-bundle-card-cr-v7 ${isCR ? "d2-bundle-card-selected-v7" : ""}`} data-testid="d2-plan-chillrefer">
          <span className="d2-bundle-tier-tag">Test the waters</span>
          <h3 className="d2-bundle-card-name">ChillRefer</h3>
          <div className="d2-bundle-price-row">
            <span className="d2-bundle-price">$99</span>
            <span className="d2-bundle-period">/ month</span>
          </div>
          <p className="d2-bundle-tagline">Automation only. No guarantee. Cancel anytime.</p>
          <ul className="d2-bundle-features">
            <li><Check className="w-4 h-4" /> 600+ recruiter outreaches / month</li>
            <li><Check className="w-4 h-4" /> AI drafts, you stay in control</li>
            <li><Check className="w-4 h-4" /> Auto follow-ups every 24h</li>
            <li><Check className="w-4 h-4" /> Live kanban CRM</li>
            <li className="d2-bundle-feature-muted"><X className="w-4 h-4" /> No human team</li>
            <li className="d2-bundle-feature-muted"><X className="w-4 h-4" /> No interview guarantee</li>
          </ul>
          <Button
            className={`d2-bundle-cta ${isCR ? "d2-bundle-cta-active" : ""}`}
            style={{ background: isCR ? "#7F77DD" : "#2b1d6e" }}
            onClick={() => actions.setSelectedClose(isCR ? null : "cr")}
            data-testid="d2-select-chillrefer"
          >
            {isCR ? "Selected" : "Start with ChillRefer"}
          </Button>
        </div>

        {/* PLAN 2 — Hired in 90 — value stack only */}
        <div className={`d2-bundle-card-v7 d2-bundle-card-premium-v7 ${isH90 ? "d2-bundle-card-selected-v7" : ""}`} data-testid="d2-plan-hired-in-90">
          <span className="d2-bundle-best-badge">Most popular</span>
          <span className="d2-bundle-tier-tag">Full done-for-you</span>
          <h3 className="d2-bundle-card-name">Hired in 90</h3>

          <div className="d2-vs-v7">
            <div className="d2-vs-row-v7">
              <span>Real human application team (90-day)</span>
              <span className="d2-vs-val">$2,400</span>
            </div>
            <div className="d2-vs-row-v7">
              <span>1:1 Personal Closer Coach</span>
              <span className="d2-vs-val">$1,800</span>
            </div>
            <div className="d2-vs-row-v7">
              <span>Resume rebuild + dashboard</span>
              <span className="d2-vs-val">$600</span>
            </div>
            <div className="d2-vs-row-v7">
              <span>1-year ChillRefer fallback</span>
              <span className="d2-vs-val">$1,188</span>
            </div>
            <div className="d2-vs-row-v7 d2-vs-row-bonus-v7">
              <span><Sparkles className="w-3 h-3 inline mr-1" /> Hired-in-90 Promise</span>
              <span className="d2-vs-val">priceless</span>
            </div>
            <div className="d2-vs-total-v7">
              <span>TOTAL VALUE</span>
              <span className="d2-vs-strike">$5,988</span>
            </div>
            <div className="d2-vs-today-v7">
              <span>Today's price</span>
              <span className="d2-vs-today-num">$1,499</span>
            </div>
            <p className="d2-vs-foot-v7">
              <em>Priced at $1,499 because we'd rather have 100 successful clients than 10 expensive ones.</em>
            </p>
          </div>
        </div>

      </div>

      {/* PAY BLOCK — horizontal strip below both cards (always visible, no scroll) */}
      <div className="d2-pay-strip-v7" data-testid="d2-pay-strip">
        <div className="d2-pay-strip-head">
          <Sparkles className="w-4 h-4" />
          <span>For Hired in 90 — pick how you want to pay</span>
        </div>
        <div className="d2-pay-strip-options">
          <button
            className={`d2-pay-strip-opt ${selectedClose === "h90_a" ? "d2-pay-strip-opt-active" : ""}`}
            onClick={() => actions.setSelectedClose("h90_a")}
            data-testid="d2-pay-option-a"
          >
            <span className="d2-pay-strip-letter">A</span>
            <div className="d2-pay-strip-body">
              <div className="d2-pay-strip-name">Standard split</div>
              <div className="d2-pay-strip-detail">$750 today + $750 after 1st interview</div>
            </div>
            <span className="d2-pay-strip-pop">Most popular</span>
          </button>

          <button
            className={`d2-pay-strip-opt ${selectedClose === "h90_c" ? "d2-pay-strip-opt-active" : ""}`}
            onClick={() => actions.setSelectedClose("h90_c")}
            data-testid="d2-pay-option-c"
          >
            <span className="d2-pay-strip-letter">B</span>
            <div className="d2-pay-strip-body">
              <div className="d2-pay-strip-name">Pay in full</div>
              <div className="d2-pay-strip-detail">$1,299 today, save $200</div>
            </div>
            <span className="d2-pay-strip-save">Save $200</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── 16. Skeptic Testimonial — DYNAMIC, dropdown shows ALL case studies ── */
function S16SkepticTestimonial({ state, actions }) {
  const { selectedCaseStudies = [], lead } = state;
  const fallback = CASE_STUDIES.find((c) => c.id === 8) || CASE_STUDIES[0];
  const [activeId, setActiveId] = useState(null);

  // Determine active case study with priority:
  //   1. closer's manual override (activeId)
  //   2. first selected case study (closer's pick on S13)
  //   3. region-match: any case study where market matches lead's market
  //   4. fallback (Aman)
  const leadMarket = (state.confirmedMarket || lead?.targetMarket || "").toLowerCase().trim();
  const fromSelected = selectedCaseStudies.length > 0
    ? CASE_STUDIES.find((c) => c.id === selectedCaseStudies[0])
    : null;
  const regionMatch = leadMarket
    ? CASE_STUDIES.find((c) => (c.market || "").toLowerCase().includes(leadMarket) || leadMarket.includes((c.market || "").toLowerCase()))
    : null;
  const cs = (activeId && CASE_STUDIES.find((c) => c.id === activeId))
    || fromSelected
    || regionMatch
    || fallback;

  // Reset manual pick whenever selectedCaseStudies changes (closer re-picked on S13)
  useEffect(() => {
    setActiveId(null);
  }, [selectedCaseStudies.length, selectedCaseStudies.join(",")]);

  return (
    <div className="demo-screen d2-fit-screen d2-skeptic-screen" data-testid="d2-screen-16">
      <div className="d2-skeptic-wrap">
        <div className="d2-skeptic-top-row">
          <div>
            <span className="d2-skeptic-eyebrow">
              <MessageSquare className="w-4 h-4 inline mr-1" /> Almost said no
            </span>
            <h2 className="d2-skeptic-title">
              {state.lead.clientName}, you might be where {cs.name} was {cs.duration || "a few weeks"} ago.
            </h2>
          </div>
          {/* ALWAYS visible — closer can pick from any case study */}
          <select
            className="d2-skeptic-selector"
            value={cs.id}
            onChange={(e) => setActiveId(parseInt(e.target.value, 10))}
            data-testid="d2-skeptic-selector"
            title="Pick the case study that matches this lead's pain best"
          >
            <optgroup label={selectedCaseStudies.length > 0 ? "Selected on Proof Wall" : "All case studies"}>
              {(selectedCaseStudies.length > 0
                ? CASE_STUDIES.filter((c) => selectedCaseStudies.includes(c.id))
                : CASE_STUDIES
              ).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.tag}
                </option>
              ))}
            </optgroup>
            {selectedCaseStudies.length > 0 && (
              <optgroup label="All other case studies">
                {CASE_STUDIES.filter((c) => !selectedCaseStudies.includes(c.id)).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.tag}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
        <p className="d2-skeptic-sub">
          Real client. Real WhatsApp. {cs.name.split(" ")[0]} almost didn't sign up. Here's what happened next.
        </p>

        <div className="d2-skeptic-grid">
          <div className="d2-skeptic-side d2-skeptic-side-bad">
            <div className="d2-skeptic-side-head">
              <span className="d2-skeptic-side-tag">Before — the hesitation</span>
            </div>
            <div className="d2-skeptic-quote">
              "{cs.chat?.[0]?.text || cs.painPoint}"
            </div>
            <div className="d2-skeptic-tags">
              <span className="d2-skeptic-tag-pill">{cs.tag}</span>
              <span className="d2-skeptic-tag-pill">{cs.role}</span>
              <span className="d2-skeptic-tag-pill">{cs.market}</span>
            </div>
            <div className="d2-skeptic-attribution">
              — {cs.name}, on first WhatsApp message
            </div>
          </div>

          <div className="d2-skeptic-side d2-skeptic-side-good">
            <div className="d2-skeptic-side-head">
              <span className="d2-skeptic-side-tag">After — {cs.duration || "a few weeks"} later</span>
            </div>
            <div className="d2-skeptic-result">
              <div className="d2-skeptic-result-stat">
                <div className="d2-skeptic-result-num">{cs.interviews}</div>
                <div className="d2-skeptic-result-lbl">interviews</div>
              </div>
              <div className="d2-skeptic-result-text">
                "{cs.chat?.[cs.chat.length - 1]?.text || cs.result}"
              </div>
            </div>
            {cs.screenshotUrl && (
              <div className="d2-skeptic-screenshot-wrap">
                <img
                  src={cs.screenshotUrl}
                  alt={`${cs.name} WhatsApp conversation`}
                  className="d2-skeptic-screenshot"
                  data-testid="d2-skeptic-screenshot"
                />
                <span className="d2-skeptic-screenshot-tag">Real WhatsApp · {cs.name}</span>
              </div>
            )}
          </div>
        </div>

        <p className="d2-skeptic-foot">
          {cs.name} almost said no. Today {cs.name.split(" ")[0]} is hired.
        </p>
      </div>
    </div>
  );
}

/* ─── 19. Close & Pay (was S18) ── */
function S19Close({ state, actions }) {
  const { selectedClose, lead } = state;
  const OPTIONS = [
    { key: "cr",    name: "ChillRefer only",                desc: "Automation only. No human team, no resume rebuild, no guarantee.", price: "$99",    unit: "/mo",   accent: "#7F77DD" },
    { key: "h90_a", name: "Hired in 90 — Standard split",   desc: "$750 today + $750 after first interview. Most popular.",          price: "$1,499", unit: "total", accent: "#1D9E75", saved: "Most popular" },
    { key: "h90_c", name: "Hired in 90 — Pay in full",      desc: "$1,299 today, one payment, $200 off.",                             price: "$1,299", unit: "today", accent: "#0f172a", saved: "Save $200" },
  ];
  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-19">
      <h2 className="screen-heading-v2 d2-tight-h" style={{ textAlign: "center" }}>Pick your move, {lead.clientName}</h2>
      <p className="d2-close-title">Two products. Same $1,499 total for Hired in 90 — pick the payment psychology that fits.</p>
      <div className="d2-close-wrap">
        <div className="d2-close-pro-options">
          {OPTIONS.map((opt) => (
            <button
              key={opt.key}
              className={`d2-close-pro-option ${selectedClose === opt.key ? "d2-close-pro-active" : ""}`}
              onClick={() => actions.setSelectedClose(opt.key)}
              data-testid={`d2-close-opt-${opt.key}`}
              style={{ borderColor: selectedClose === opt.key ? opt.accent : undefined }}
            >
              <span className="d2-close-pro-radio" style={{ borderColor: selectedClose === opt.key ? opt.accent : undefined, background: selectedClose === opt.key ? opt.accent : undefined }} />
              <div className="d2-close-pro-body">
                <div className="d2-close-pro-name">{opt.name}</div>
                <div className="d2-close-pro-desc">{opt.desc}</div>
              </div>
              <div className="d2-close-pro-price-wrap">
                <span className="d2-close-pro-price" style={{ color: opt.accent }}>{opt.price}<small>{opt.unit}</small></span>
                {opt.saved && <span className="d2-close-pro-save">{opt.saved}</span>}
              </div>
            </button>
          ))}
        </div>
        {selectedClose && (
          <div className="d2-close-summary" data-testid="d2-close-summary">
            Confirm in the right panel. We'll move <strong>{lead.clientName}</strong> to a <strong>{OPTIONS.find(o => o.key === selectedClose)?.name}</strong> close.
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── 17. Objection Crusher — pre-empt the unspoken ── */
function S17ObjectionCrusher({ state }) {
  const { lead } = state;
  const months = state.monthsSearching || lead.monthsSearching || "a few";
  const market = fmt(state.confirmedMarket || lead.targetMarket, "your market");
  const role = fmt(state.confirmedRole || lead.targetRole, "your role");

  // Disqualification: simulate "spots left this month" — feels real, creates urgency
  const spotsTaken = 22;
  const spotsTotal = 30;
  const spotsLeft = spotsTotal - spotsTaken;

  const OBJECTIONS = [
    {
      thought: "$1,499 is a lot of money for me right now.",
      truth: `It's less than 1 month of your future ${role} paycheck. Pays itself back the moment you accept your first offer.`,
      bg: "#fef3c7",
      truthBg: "#fffbeb",
      accent: "#b45309",
    },
    {
      thought: `I'll just keep trying on my own. I'll figure it out.`,
      truth: `You already have, ${lead.clientName}. ${months} months in, same plan, same result. A different next 90 days needs a different system.`,
      bg: "#fee2e2",
      truthBg: "#fef2f2",
      accent: "#b91c1c",
    },
    {
      thought: "What if it doesn't work for me? What if I'm the exception?",
      truth: "Then we owe you 1 full year of ChillRefer free until you land. That's the Hired-in-90 Promise. Your downside is literally zero.",
      bg: "#dcfce7",
      truthBg: "#f0fdf4",
      accent: "#15803d",
    },
    {
      thought: "I need to talk to my spouse / partner / family before deciding.",
      truth: "Totally fair. I'll email you a 2-page summary right after this call. Most spouses say yes the moment they see the math.",
      bg: "#dbeafe",
      truthBg: "#eff6ff",
      accent: "#1d4ed8",
    },
  ];

  return (
    <div className="demo-screen d2-fit-screen d2-objection-screen" data-testid="d2-screen-17">
      <div className="d2-objection-wrap">
        <span className="d2-objection-eyebrow">
          <MessageCircle className="w-4 h-4 inline mr-1" /> Reading your mind for a moment
        </span>
        <h2 className="d2-objection-title">
          {lead.clientName}, right now you're probably thinking one of these 4 things.
        </h2>
        <p className="d2-objection-sub">
          Let me deal with each before you have to say it out loud.
        </p>

        <div className="d2-objection-grid" data-testid="d2-objection-grid">
          {OBJECTIONS.map((o, i) => (
            <div
              key={i}
              className="d2-objection-card"
              data-testid={`d2-objection-${i + 1}`}
              style={{ borderColor: o.accent }}
            >
              <div className="d2-objection-thought" style={{ background: o.bg }}>
                <div className="d2-objection-thought-label" style={{ color: o.accent }}>
                  <MessageCircle className="w-3.5 h-3.5" /> What you're thinking
                </div>
                <div className="d2-objection-thought-text">"{o.thought}"</div>
              </div>
              <div className="d2-objection-truth" style={{ background: o.truthBg }}>
                <div className="d2-objection-truth-label" style={{ color: o.accent }}>
                  <Check className="w-3.5 h-3.5" /> Here's the truth
                </div>
                <div className="d2-objection-truth-text">{o.truth}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Disqualification / take-away strip */}
        <div className="d2-objection-takeaway" data-testid="d2-objection-takeaway">
          <Lock className="w-5 h-5" />
          <div>
            <div className="d2-objection-takeaway-head">One last thing before you decide…</div>
            <div className="d2-objection-takeaway-body">
              We only take <strong>{spotsTotal} Hired-in-90 clients per market per month</strong>. We're at <strong>{spotsTaken} in {market}</strong> right now. If we hit {spotsTotal} this week, the next slot is in March. If you're a fit, I want you in. If you're not ready, that's fine — there's no hard sell here.
            </div>
            <div className="d2-objection-takeaway-spots">
              <span className="d2-objection-spots-num">{spotsLeft}</span>
              <span className="d2-objection-spots-lbl">spots left in {market} · this month</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─── 18. What Happens After (was S17) ── */
function S18WhatHappensAfter({ state }) {
  const { lead, selectedClose } = state;
  const isCR = selectedClose === "cr";
  return (
    <div className="demo-screen d2-fit-screen d2-after-screen" data-testid="d2-screen-18">
      <h2 className="screen-heading-v2 d2-tight-h" style={{ textAlign: "center" }}>What happens the second you pay</h2>
      <p className="screen-subheading d2-tight-sub" style={{ textAlign: "center" }}>
        No "we'll get back to you in 48 hours." Here's the real first hour.
      </p>

      <div className="d2-after-grid">

        <div className="d2-after-step" data-testid="d2-after-step-1">
          <div className="d2-after-step-num">01</div>
          <div className="d2-after-mockup d2-after-mockup-checkout">
            <div className="d2-after-mockup-bar">
              <div className="d2-after-mockup-dot" style={{ background: "#ef4444" }} />
              <div className="d2-after-mockup-dot" style={{ background: "#f59e0b" }} />
              <div className="d2-after-mockup-dot" style={{ background: "#22c55e" }} />
              <span className="d2-after-mockup-url"><Lock className="w-3 h-3 inline mr-1" /> Secure checkout</span>
            </div>
            <div className="d2-after-stripe-body">
              <div className="d2-after-stripe-merchant">BulkJobApply · Order summary</div>
              <div className="d2-after-stripe-amount">{isCR ? "US$99.00" : "US$1,499.00"}</div>
              <div className="d2-after-stripe-line"><span>Plan</span><span>{isCR ? "ChillRefer" : "Hired in 90"}</span></div>
              <div className="d2-after-stripe-line"><span>Billing</span><span>{isCR ? "Monthly" : "Today"}</span></div>
              <div className="d2-after-stripe-pay">Confirm & pay {isCR ? "US$99" : "US$1,499"}</div>
            </div>
          </div>
          <div className="d2-after-step-name">Secure receipt arrives</div>
          <div className="d2-after-step-desc">Bank-grade checkout. Saved card auto-fills. 30 seconds, you're done.</div>
        </div>

        <div className="d2-after-step" data-testid="d2-after-step-2">
          <div className="d2-after-step-num">02</div>
          <div className="d2-after-mockup d2-after-mockup-wa">
            <div className="d2-after-wa-header">
              <div className="d2-after-wa-avatar">B</div>
              <div className="d2-after-wa-name">BulkJobApply Team</div>
              <div className="d2-after-wa-status">online</div>
            </div>
            <div className="d2-after-wa-body">
              <div className="d2-after-wa-bubble d2-after-wa-bubble-in">
                Welcome to the team, {lead.clientName}! 🎉<br/>
                Payment received.
              </div>
              {isCR ? (
                <div className="d2-after-wa-bubble d2-after-wa-bubble-in">
                  Your ChillRefer dashboard is being provisioned. Login link drops within the hour.
                </div>
              ) : (
                <>
                  <div className="d2-after-wa-bubble d2-after-wa-bubble-in">
                    Your dashboard link drops within 60 minutes.
                  </div>
                  <div className="d2-after-wa-bubble d2-after-wa-bubble-in">
                    Your dedicated WhatsApp group is being created. Your account manager will tag you in shortly.
                  </div>
                </>
              )}
              <div className="d2-after-wa-time">just now</div>
            </div>
          </div>
          <div className="d2-after-step-name">WhatsApp DM lands</div>
          <div className="d2-after-step-desc">
            Within 2 minutes. Real humans. {isCR ? "App access — direct support over WhatsApp DM (no group)." : "Plus your team group goes live within an hour."}
          </div>
        </div>

        <div className="d2-after-step" data-testid="d2-after-step-3">
          <div className="d2-after-step-num">03</div>
          <div className="d2-after-mockup d2-after-mockup-dash">
            <div className="d2-after-dash-bar">
              <span className="d2-after-dash-logo">BJA</span>
              <span className="d2-after-dash-route">Dashboard / Referrals</span>
            </div>
            <div className="d2-after-dash-stats">
              <div className="d2-after-dash-stat"><div className="d2-after-dash-stat-num">30</div><div className="d2-after-dash-stat-lbl">Queued</div></div>
              <div className="d2-after-dash-stat"><div className="d2-after-dash-stat-num">12</div><div className="d2-after-dash-stat-lbl">Sent today</div></div>
              <div className="d2-after-dash-stat"><div className="d2-after-dash-stat-num">3</div><div className="d2-after-dash-stat-lbl">Replied</div></div>
            </div>
            <div className="d2-after-dash-rows">
              <div className="d2-after-dash-row"><span>Microsoft · Sr. Recruiter</span><span className="d2-after-dash-pill d2-after-dash-pill-warm">Warm</span></div>
              <div className="d2-after-dash-row"><span>Stripe · Engineering Manager</span><span className="d2-after-dash-pill d2-after-dash-pill-sent">Sent</span></div>
              <div className="d2-after-dash-row"><span>Atlassian · Talent Lead</span><span className="d2-after-dash-pill d2-after-dash-pill-queued">Queued</span></div>
            </div>
          </div>
          <div className="d2-after-step-name">First 30 referrers queued</div>
          <div className="d2-after-step-desc">Within 24 hours. ChillRefer pre-fills your pipeline so day 1 has motion.</div>
        </div>

        <div className="d2-after-step" data-testid="d2-after-step-4">
          <div className="d2-after-step-num">04</div>
          <div className="d2-after-mockup d2-after-mockup-gmail">
            <div className="d2-after-gmail-bar">
              <Mail className="w-3.5 h-3.5" />
              <span>Inbox · 1 unread</span>
            </div>
            <div className="d2-after-gmail-row d2-after-gmail-row-unread">
              <div className="d2-after-gmail-from">Sarah · Stripe</div>
              <div className="d2-after-gmail-subject">Quick chat about the {fmt(state.confirmedRole || lead.targetRole, "role")}?</div>
              <div className="d2-after-gmail-snippet">Hey {lead.clientName} — saw your referral. Loved the background. Got 30 mins this week to chat?…</div>
              <div className="d2-after-gmail-time">9:14 AM</div>
            </div>
            <div className="d2-after-gmail-row">
              <div className="d2-after-gmail-from">Recruiter · Notion</div>
              <div className="d2-after-gmail-subject">Re: your application</div>
              <div className="d2-after-gmail-snippet">Thanks for the warm intro from Marc. Pulling your profile now…</div>
              <div className="d2-after-gmail-time">8:01 AM</div>
            </div>
          </div>
          <div className="d2-after-step-name">First interview email</div>
          <div className="d2-after-step-desc">Most clients see this within 2–3 weeks. Sometimes within days.</div>
        </div>
      </div>
    </div>
  );
}

/* ─── 20. Thank You (was S19) ── */
function S20ThankYou({ state, actions }) {
  const { selectedClose, lead, settings, auth, selectedCaseStudies = [] } = state;
  const [pdfStatus, setPdfStatus] = useState("idle");
  const [pdfError, setPdfError] = useState("");

  const labelMap = {
    cr:    "ChillRefer ($99/mo)",
    h90_a: "Hired in 90 — Standard split ($750 + $750)",
    h90_c: "Hired in 90 — Pay in full ($1,299, save $200)",
  };
  const planLabel = selectedClose ? labelMap[selectedClose] : "Plan not selected yet";
  const isCRonly = selectedClose === "cr";

  const handleGeneratePDF = async () => {
    setPdfStatus("loading");
    setPdfError("");
    try {
      const planMap = {
        cr:    "chillrefer-99",
        h90_a: "hired-in-90",
        h90_c: "hired-in-90",
      };
      const leadForPdf = {
        ...lead,
        targetRole: state.confirmedRole || lead.targetRole || "",
        targetMarket: state.confirmedMarket || lead.targetMarket || "",
        recommendedPlan: selectedClose ? planMap[selectedClose] : "hired-in-90",
        targetSalary: lead.targetSalary || null,
      };
      await generateDemoSummaryPDF(leadForPdf, settings, auth, selectedCaseStudies);
      setPdfStatus("done");
    } catch (e) {
      console.error("PDF generation failed:", e);
      setPdfError(e.message || "Could not generate PDF");
      setPdfStatus("error");
    }
  };

  return (
    <div className="demo-screen d2-fit-screen" data-testid="d2-screen-20">
      <div className="d2-thanks-wrap">
        <div className="d2-thanks-icon">
          <PartyPopper className="w-10 h-10" />
        </div>
        <h1 className="d2-thanks-title">Welcome to the team, {lead.clientName}.</h1>
        <p className="d2-thanks-sub">
          You picked <strong>{planLabel}</strong>. Payment link goes out on WhatsApp in 2 minutes.
        </p>

        <div className="d2-thanks-next">
          <h3>What happens next</h3>
          <ol>
            <li>Payment link arrives on WhatsApp within 2 minutes</li>
            {isCRonly ? (
              <>
                <li>Once paid, your ChillRefer account is provisioned within 1 hour</li>
                <li>App access only — direct support over WhatsApp DM (no group for $99 plan)</li>
                <li>First referrals begin landing within the first week</li>
              </>
            ) : (
              <>
                <li>Once paid, your dedicated WhatsApp group goes live within 1 hour</li>
                <li>Resume rebuild + 780 apps/month + ChillRefer all start on Day 1</li>
                <li>First interview calls within the first 2 weeks</li>
                <li><strong>10 interviews guaranteed in 90 days, or 1 year of ChillRefer free</strong></li>
              </>
            )}
          </ol>
        </div>

        <div className="d2-thanks-actions">
          <Button onClick={handleGeneratePDF} disabled={pdfStatus === "loading"} className="d2-thanks-pdf" data-testid="d2-generate-pdf">
            <Download className="w-4 h-4" />
            {pdfStatus === "loading" ? "Generating..." : pdfStatus === "done" ? "PDF generated — re-download" : "Download summary PDF"}
          </Button>
          <Button onClick={actions.endDemo} className="d2-thanks-end" data-testid="d2-end-thanks">
            <Check className="w-4 h-4 mr-1" /> End demo
          </Button>
        </div>
        {pdfError && <p className="d2-thanks-error">{pdfError}</p>}
        {pdfStatus === "done" && <p className="d2-thanks-success">PDF saved to downloads. Send it on WhatsApp before ending.</p>}
      </div>
    </div>
  );
}
